/**
 * Corramet-BP Roof Calculator
 * Reads from window.roofCalcData (pre-filled by Liquid snippet)
 *
 * PURE METAOBJECT INTERPRETER:
 *  - variantDrivenBy selects the variant
 *  - qtyFormula computes the quantity
 *  - No business logic in JS — all driven by metaobject values
 *  - Promo messages render inline beneath any rule that has one
 *
 * KEY DIFFERENCE FROM CORRUGATED BITUMEN JS:
 *  - Three colours: Black, Green, Clear
 *  - Clear selects a separate sheet product (P2) and separate fixings product (P4)
 *  - getFlashingColour() maps state.colour 'Clear' → 'White' for flashing/accessory products
 *  - Null-guard on variant match handles colour-gating (no applicable_colours field needed)
 *
 * SHEET CALC:
 *  - Width: CEIL(A / (sheetWidthMm - sheetWidthOverlapMm))  → CEIL(A / 1000) for BP
 *  - Length: minimum-wastage split with sheetLengthOverlapMm (200mm for BP)
 *
 * FLASHING CALC:
 *  - Smallest variant covering measurement, or split if needed (no overlap — butt join)
 *  - Colour resolved via getFlashingColour()
 *
 * IMAGE NAMING: {calcKey}-{roofKeyDashed}-{colour}.webp
 *  - colour token: black, green, clear
 */

(function () {
  'use strict';

  var data = window.roofCalcData;
  if (!data) {
    console.warn('Roof calculator: no data found. Check Liquid snippet.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────
  var state = {
    tag1: '',
    rfnm: 1,
    roofKey: '',
    roofName: '',
    colour: '',            // blank by default — user must select
    sizeA: 0,
    sizeB: 0,
    sheetsAcross: 0,
    lengthRun: [],         // set by sheet rule; read by fixings rule
    cartItems: []
  };


  // ─── Flashing colour resolver ────────────────────────────────────────
  // Clear sheets always use White for flashing/accessory products
  function getFlashingColour() {
    return state.colour.toLowerCase() === 'clear' ? 'White' : state.colour;
  }


  // ─── Split functions ─────────────────────────────────────────────────

  // Flashing split (no overlap — butt join)
  function splitLength(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    var results = [];
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        results.push({ variantLabel: sorted[i].variantLabel, quantity: 1 });
        return results;
      }
    }
    var longest = sorted[sorted.length - 1];
    var fullLengths = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    if (fullLengths > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullLengths });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  // Sheet split (with overlap)
  // Divide by longest tier, handle remainder with smallest covering tier.
  // No upper limit — repeats longest tier as many times as needed.
  function splitSheetLength(tiers, measurementMm, overlapMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    var longest = sorted[sorted.length - 1];
    var run = [];

    // Single sheet covers measurement
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        run.push({ variantLabel: sorted[i].variantLabel, mm: sorted[i].maxLength });
        return run;
      }
    }

    // Multiple sheets needed — divide by longest, handle remainder
    var remaining = measurementMm;
    while (remaining > 0) {
      if (remaining <= longest.maxLength) {
        // Find smallest tier that covers the remainder
        for (var j = 0; j < sorted.length; j++) {
          if (remaining <= sorted[j].maxLength) {
            run.push({ variantLabel: sorted[j].variantLabel, mm: sorted[j].maxLength });
            break;
          }
        }
        break;
      } else {
        run.push({ variantLabel: longest.variantLabel, mm: longest.maxLength });
        remaining = remaining - longest.maxLength + overlapMm;
      }
    }

    return run;
  }


  // ─── Helpers ─────────────────────────────────────────────────────────

  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    return overrides[state.roofKey] || null;
  }

  function getProduct(rule) {
    var products = rule.products;
    if (!products || products.length === 0) return null;
    return products[0];
  }

  // FR locale variant title normalisation
  // Shopify translates variant option values — map FR colour words back to EN for matching
  var colourMap = {
    'noir': 'black', 'vert': 'green', 'clair': 'clear',
    'blanc': 'white', 'transparente': 'clear', 'gris': 'grey', 'rouge': 'red', 'brun': 'brown'
  };

  function normaliseTitle(title) {
    var t = title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var fr in colourMap) {
      if (colourMap.hasOwnProperty(fr)) {
        t = t.replace(new RegExp(fr, 'g'), colourMap[fr]);
      }
    }
    return t;
  }

  function findVariantByLabel(product, label) {
    if (!product || !product.variants) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = normaliseTitle(product.variants[i].title);
      if (normTitle.indexOf(normLabel) !== -1) return product.variants[i];
    }
    return null;
  }

  // Match by colour + length (used for coloured sheets and flashings)
  // When lengthLabel is empty, matches by colour only (colour_fixed strategy)
  // Uses getFlashingColour() so Clear maps to White for accessory products
  // normaliseTitle() maps FR translated variant values back to EN for matching
  function findColouredVariant(product, lengthLabel, colour) {
    if (!product || !product.variants) return null;
    var normLen = lengthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    var normCol = colour.replace(/\s/g, '').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = normaliseTitle(product.variants[i].title);
      if (normLen === '') {
        if (normTitle.indexOf(normCol) !== -1) return product.variants[i];
      } else {
        if (normTitle.indexOf(normLen) !== -1 && normTitle.indexOf(normCol) !== -1) return product.variants[i];
      }
    }
    if (normLen !== '') return findVariantByLabel(product, lengthLabel);
    return product.variants[0]; // fallback for colour_fixed with no match
  }


  // ─── Core calculation ────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    state.lengthRun = [];
    state.sheetsAcross = Math.ceil(state.sizeA / (data.sheetWidthMm - data.sheetWidthOverlapMm));

    for (var i = 0; i < data.productRules.length; i++) {
      processRule(data.productRules[i]);
    }

    renderKitList();
  }

  // Colour applicability gate — reads applies_when field
  // Fail-open: empty or unrecognised values always pass
  function appliesToColour(rule) {
    var aw = rule.appliesWhen;
    if (!aw || aw === 'always') return true;
    if (aw === 'colour_clear') return state.colour.toLowerCase() === 'clear';
    if (aw === 'colour_black_green') return state.colour.toLowerCase() !== 'clear';
    return true;
  }

  function processRule(rule) {
    if (!appliesToColour(rule)) return;

    var override = getOverride(rule);
    if (!override) return;

    var product = getProduct(rule);
    if (!product) return;

    var formula = rule.qtyFormula;
    var variantDrivenBy = rule.variantDrivenBy;
    var apexMultiplier = override.multiplier || 1;
    var quantity = 0;
    var variant = null;

    // ── Sheet split (colour + length) ──
    // For coloured sheets: findColouredVariant matches state.colour + length
    // For clear sheets: separate product, findColouredVariant falls back to length-only match
    if (variantDrivenBy === 'size_b_split_length' && formula === 'ceil_a_div_coverage') {
      state.lengthRun = splitSheetLength(rule.lengthTiers, state.sizeB, data.sheetLengthOverlapMm);
      var bucket = {};
      for (var k = 0; k < state.lengthRun.length; k++) {
        bucket[state.lengthRun[k].variantLabel] = (bucket[state.lengthRun[k].variantLabel] || 0) + 1;
      }
      for (var lbl in bucket) {
        if (!bucket.hasOwnProperty(lbl)) continue;
        variant = findColouredVariant(product, lbl, state.colour);
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: state.sheetsAcross * bucket[lbl] * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Flashing split × B (no overlap) ──
    // getFlashingColour() maps Clear → White
    if (variantDrivenBy === 'size_b_split_length' && formula === 'split_length_from_b') {
      var splitsB = splitLength(rule.lengthTiers, state.sizeB);
      for (var sb = 0; sb < splitsB.length; sb++) {
        variant = findColouredVariant(product, splitsB[sb].variantLabel, getFlashingColour());
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: splitsB[sb].quantity * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Flashing split × A (no overlap) ──
    // getFlashingColour() maps Clear → White
    if (variantDrivenBy === 'size_a_split_length' && formula === 'split_length_from_a') {
      var splitsA = splitLength(rule.lengthTiers, state.sizeA);
      for (var sa = 0; sa < splitsA.length; sa++) {
        variant = findColouredVariant(product, splitsA[sa].variantLabel, getFlashingColour());
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: splitsA[sa].quantity * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Colour fixed — colour variants, no length dimension ──
    // Used by P3 (coloured fixings) and P10 (coloured endcap)
    // state.colour is used directly — null-guard skips this rule when colour is Clear
    if (variantDrivenBy === 'colour_fixed') {
      variant = findColouredVariant(product, rule.fixedVariantLabel || '', state.colour);
    }

    // ── Fixed variant — no colour or length dimension ──
    // Used by P4 (clear fixings), P7 (foam filler), P11 (clear endcap)
    // Always fires regardless of colour
    if (variantDrivenBy === 'fixed') {
      variant = rule.fixedVariantLabel
        ? findVariantByLabel(product, rule.fixedVariantLabel)
        : product.variants[0];
    }

    if (!variant) return;

    // ── Quantity formula ───────────────────────────────────────────────

    if (formula === 'fixings_per_sheet_run') {
      var totalFixings = 0;
      for (var m = 0; m < state.lengthRun.length; m++) {
        totalFixings += (rule.perSheetQty || 10) * (state.lengthRun[m].mm / 1000);
      }
      var packSize = rule.qtyMultiplierDefault || 50;
      quantity = Math.ceil(totalFixings * state.sheetsAcross * apexMultiplier / packSize);

    } else if (formula === 'per_sheet') {
      quantity = state.sheetsAcross * (rule.perSheetQty || 1) * apexMultiplier;

    } else if (formula === 'fixed') {
      quantity = override.fixed_qty || 0;
    }

    if (quantity > 0) {
      state.cartItems.push({
        rule: rule, product: product, variant: variant,
        quantity: quantity, editable: true
      });
    }
  }


  // ─── Kit list rendering ──────────────────────────────────────────────

  function renderKitList() {
    var container = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) { console.warn('Roof calculator: .kit-list container not found'); return; }

    var html = '', mhtml = '', total = 0, totalSaved = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      var price = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      var lineSaving = compareAt ? (compareAt - price) * item.quantity : 0;
      total += lineTotal;
      totalSaved += lineSaving;

      var image = '';
      if (item.variant && item.variant.image) image = item.variant.image;
      else if (item.product && item.product.images && item.product.images.length > 0) image = item.product.images[0];

      // Desktop
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">';
      if (image) html += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      html += '  </div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '    <span>' + formatMoney(price) + '</span>';
      }
      html += '  </div></div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25"><div class="kit-item-image">';
      if (image) mhtml += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      mhtml += '  </div></div><div class="w-75">';
      mhtml += '  <div class="kit-item-details">';
      mhtml += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '    <span>' + formatMoney(price) + '</span>';
      }
      mhtml += '  </div></div>';
      mhtml += '  <div class="kit-item-qty">';
      mhtml += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '  </div></div></div>';
      mhtml += '</div>';

      // Promo message
      if (item.rule.promoMessage) {
        html += '<div class="kit-promo-message"><span>✓ ' + item.rule.promoMessage + '</span></div>';
        mhtml += '<div class="kit-promo-message"><span>✓ ' + item.rule.promoMessage + '</span></div>';
      }
    }

    container.innerHTML = html;
    mcontainer.innerHTML = mhtml;
    $('.kitcart .total span').html(formatMoney(total));
    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }
    showStep('kit');
    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) { state.cartItems[idx].quantity--; renderKitList(); }
      });
    });
    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          b.textContent = window.location.pathname.indexOf('fr-fr') === 1 ? 'Ajout au panier...' : 'Adding to cart...';
        });
        addToCart();
      });
    });
  }

  function formatMoney(amount) {
    var currency = data.currency || {};
    try {
      return new Intl.NumberFormat(currency.locale || 'en-GB', { style: 'currency', currency: currency.code || 'GBP' }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Cart submission ──────────────────────────────────────────────────

  function addToCart() {
    var items = [];
    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR': state.colour,
          '_LENGTH A': state.sizeA,
          '_LENGTH B': state.sizeB
        }
      });
    }
    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled'); btn.textContent = 'Add full kit to cart';
      });
      return;
    }
    items.reverse();
    jQuery.post('/cart/add.js', { items: items }).then(function () {
      window.location.href = window.location.pathname.indexOf('fr-fr') === 1 ? '/fr-fr/cart' : '/cart';
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled'); btn.textContent = 'Add full kit to cart';
      });
    });
  }


  // ─── UI step management ───────────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    if (step === 'rooftype')       { $('.rooftype').show();        updateStepIndicators('wtor'); }
    else if (step === 'roofsheet') { $('.roofsheet').show();       updateStepIndicators('yrc');  }
    else if (step === 'roofsize')  { $('.roofsize').show();        updateStepIndicators('cyrs'); }
    else if (step === 'kit')       { $('.kit-list-wrapper').show(); updateStepIndicators('kit');  }
  }

  function updateStepIndicators(currentStep) {
    $('.roofcal .number').removeClass('current').removeClass('complete');
    var steps = ['wtor', 'yrc', 'cyrs', 'kit'];
    var currentIdx = steps.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx)        $('.roofcal .number.' + steps[i]).addClass('complete');
      else if (i === currentIdx) $('.roofcal .number.' + steps[i]).addClass('current');
    }
  }


  // ─── Image swap helper ────────────────────────────────────────────────

  function buildImagePath(currentSrc) {
    if (!state.roofKey || !state.colour) return currentSrc;
    var roofSegment = state.roofKey.replace(/_/g, '-');
    var newFilename = data.calcKey + '-' + roofSegment + '-' + state.colour.toLowerCase() + '.webp';
    return currentSrc.replace(/[^\/]+\.(webp|jpg|jpeg|png)/i, newFilename);
  }


  // ─── Event bindings ───────────────────────────────────────────────────

  $('.roofcal .roofimg').click(function () {
    var roofIndex = parseInt($(this).attr('key')) - 1;
    var roofType = data.roofTypes[roofIndex];
    if (!roofType) return;
    state.roofKey = roofType.key;
    state.roofName = roofType.name;
    state.tag1 = $(this).attr('title');
    state.rfnm = $(this).attr('key');
    var staticImage = roofType.image || $(this).find('img').attr('src');
    $('.roofsheet img').attr('key', staticImage);
    $('.roofsheet img, .roofsize img, .qimg img').attr('src', staticImage);
    showStep('roofsheet');
  });

  $('.srtype').click(function () {
    state.roofKey = ''; state.roofName = ''; showStep('rooftype');
  });

  $('select[name="colour1"]').change(function () {
    state.colour = $(this).val();
    $('.quote .clr').text(this.options[this.selectedIndex].text);
    var baseImg = $('.roofsheet img').attr('key');
    if (baseImg) {
      var newImage = buildImagePath(baseImg);
      $('.roofsheet img, .roofsize img, .qimg img').attr('src', newImage);
    }
  });

  $('.rfsize').click(function () {
    if (!state.roofKey) {
      alert(window.location.pathname.indexOf('fr-fr') > 0 ? 'Veuillez sélectionner un type de toiture' : 'Please select a Roof Type');
      return false;
    }
    if (!state.colour) {
      alert(window.location.pathname.indexOf('fr-fr') > 0 ? 'Veuillez sélectionner une couleur de tôle de toiture' : 'Please select a Roof Sheet Colour');
      return false;
    }
    showStep('roofsize');
  });

  $('.roofcal .yrc').click(function () {
    if (!state.roofKey) return false;
    showStep('roofsheet');
  });

  $('.ckit').click(function () {
    $(this).attr('disabled', 'disabled');
    state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;
    if (state.sizeA < 1000 || state.sizeB < 1000) {
      alert(data.minSizeMessage || 'Please enter valid length and width (minimum 1000mm)');
      $(this).attr('disabled', false);
      return false;
    }
    $('.quote .sizea span').text(state.sizeA);
    $('.quote .sizeb span').text(state.sizeB);
    calculateKit();
    $(this).attr('disabled', false);
  });

  $('.roofinput input').change(function () {
    $('.ckit').attr('disabled', false);
  });

})();